import { makeStyles } from "tss-react/mui";
import { Grid, FormControl, InputLabel, Select, MenuItem, Button, Divider, ListSubheader, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ResponsivePage from "../../common/core/ResponsivePage";
import { useNavigate, useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import SimpleTable from "../../common/tables/SimpleTable";
import { getCurrencyShorthand } from "../../../providers/StationProvider";
import { FixNumber, GetUserReference } from "../../../common/orders";
import { ToggleSurface } from "../../common/core/Surface";
import { ExportEnums, ExportEnumsLists, OrderEnumsLists } from "../../../common/constants";
import { convertInvoiceJournalsToFormat, downloadCSV } from "../../../common/exportformats";
import { useEntityById, useEntities, Compare, useEntityFunctions, StatusCode, useAuth, Task } from "@emberly/zenith-client";
import moment from "moment/moment";
import { useNotification } from "../../../providers/NotificationProvider";
import { IntegrationLibrary } from "../integrations/common/library";
import ExportHistorySection from "../../common/sections/ExportHistorySection";
import ConfirmDialog from "../../common/inputs/ConfirmDialog";
import config from "../../../config";

const useStyles = makeStyles()(
  (theme) => ({
    number: {
      color: theme.palette.text.disabled,
      fontWeight: "500"
    }
  })
);

const FILE_FORMATS_LEN = ExportEnumsLists.InvoiceExportFormats.length;

export default function InvoiceExportPage() {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { exportId } = useParams();

  const { alert, snackbar } = useNotification();
  const [format, setFormat] = useState(ExportEnums.InvoiceExportFormats.SO01);
  const [working, setWorking] = useState(false);
  const [resendAction, setResendAction] = useState(null);
  const { user } = useAuth();

  const { entity: exportEntity, updateEntityField } = useEntityById("InvoiceExport", exportId);
  const { entities: integrationEntities } = useEntities("Integration");
  const { emitServiceEvent: emitIntegrationEvent } = useEntityFunctions("Integration");
  const integrations = useMemo(() => integrationEntities.filter(t => !!IntegrationLibrary.get(t.serviceType)?.capabilities?.ImportInvoiceOrders), [integrationEntities]);

  useEffect(() => {
    const history = exportEntity?.history;

    if (!!history?.length && integrations.length !== 0) {
      const prevExport = history[history.length - 1];
      const index = integrations.findIndex(t => t.friendlyName === prevExport.friendlyName);

      if (index !== -1) {
        const fIndex = index + FILE_FORMATS_LEN;
        setFormat(fIndex);
      }
    }
  }, [integrations, exportEntity]);

  const { entities: journalLines, loading: journalLinesLoading } = useEntities("InvoiceJournal", useMemo(() => ({
    path: "exportId",
    comparer: Compare.EQ,
    value: exportId,
    name: `invoicejournals_${exportId}`
  }), [exportId]));

  const { entities: orders, loading: ordersLoading } = useEntities("Order", useMemo(() => ({
    path: "payment.invoice.exportId",
    comparer: Compare.EQ,
    value: exportId,
    name: `orders_invoiceexport_${exportId}`
  }), [exportId]));

  const logExport = useCallback((friendlyName) => {
    updateEntityField("history", [
      ...(exportEntity.history || []),
      {
        friendlyName,
        user: GetUserReference(user),
        created: moment.utc().toISOString()
      }
    ]);
    if (!exportEntity.exported) {
      updateEntityField("exported", true);
    }
  }, [updateEntityField, user, exportEntity]);


  const sendToIntegration = useCallback(async () => {
    try {
      snackbar(t("integrations:common:sending"));
      setWorking(true);
      const selected = integrations[format - FILE_FORMATS_LEN];
      const response = await emitIntegrationEvent("ImportInvoiceOrders", { integrationId: selected.id, exportId })

      if (response.statusCode === StatusCode.Success) {
        logExport(selected.friendlyName);

        if (!!response?.value?.actions?.length > 0) {
          await alert(
            t("integrations:common:sendingSuccess"),
            (
              <ul style={{ margin: 0, paddingLeft: "24px" }}>
                {response.value.actions.map(
                  (action, key) => <li key={key}>
                    <Typography variant="body1">
                      {action}
                    </Typography>
                  </li>
                )}
              </ul>
            ),
            "success"
          )
        } else {
          snackbar(t("integrations:common:sendingSuccess"), "success");
        }

      } else {
        snackbar(t("integrations:common:sendingError"), "error");

        if (!!response?.value) {
          await alert(
            t(`integrations:errors:${response.message}:title`),
            (response.value.detail || response.value.title || "").replace(/[^A-Z\d\s.:æøåÆØÅ]/gi, ''),
            "error"
          );

        } else if (!!response.message) {
          await alert(
            t(`integrations:errors:${response.message}:title`),
            t(`integrations:errors:${response.message}:description`),
            "error"
          );
        }
      }

    } catch (err) {
      console.log(err);
    } finally {
      setWorking(false);
    }
  }, [format, integrations, emitIntegrationEvent, exportId, alert, t, snackbar, logExport]);

  const downloadExport = useCallback(() => {
    const data = convertInvoiceJournalsToFormat(format, journalLines, exportEntity, t);
    const friendlyName = ExportEnumsLists.InvoiceExportFormats[format];
    downloadCSV(data, `invoiceexport_${friendlyName.toLowerCase()}_${exportEntity.number}.csv`);
    logExport(friendlyName);
  }, [journalLines, format, exportEntity, t, logExport]);

  const triggerExport = useCallback(async () => {

    if (exportEntity.exported) {
      try {
        const t = new Task();
        setResendAction(t)
        await t.wait();
      } catch {
        return;
      } finally {
        setResendAction(null);
      }
    }

    if (format < FILE_FORMATS_LEN) {
      downloadExport();
    } else {
      sendToIntegration();
    }
  }, [format, downloadExport, sendToIntegration, exportEntity]);

  const journalLinesColumns = useMemo(() => {
    return [
      {
        field: "exportNumber",
        type: "number",
        headerName: t("invoiceJournalEntries:fieldNumber"),
        flex: 0
      },
      {
        field: "orderNumber",
        type: "number",
        headerName: t("invoiceJournalEntries:fieldOrderNumber"),
        flex: 0
      },
      {
        field: "item.quantity",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.item?.quantity || "1"),
        valueFormatter: value => FixNumber(value),
        headerName: t("invoiceJournalEntries:fieldQuantity"),
        flex: 0
      },
      {
        field: "item.discount",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => row.item?.discount || 0,
        valueFormatter: (value) => `${value}%`,
        headerName: t("invoiceJournalEntries:fieldDiscount"),
        flex: 0
      },
      {
        field: "item.code",
        valueGetter: (_, row) => row.item?.code || "-",
        headerName: t("invoiceJournalEntries:fieldProductCode"),
        flex: 0
      },
      {
        field: "item.name",
        valueGetter: (_, row) => row.item?.name || "-",
        headerName: t("invoiceJournalEntries:fieldProductName"),
        flex: 0
      },
      {
        field: "item.price.value",
        type: "number",
        entityType: "decimal",
        valueGetter: (_, row) => Number(row.item?.price?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.item?.price?.value)} ${getCurrencyShorthand(t, params.row.item?.price?.currency)}`,
        headerName: t("invoiceJournalEntries:fieldPrice"),
        minWidth: 120,
      },
      {
        field: "sender.name",
        valueGetter: (_, row) => row.sender?.name || "-",
        headerName: t("invoiceJournalEntries:fieldSender"),
        flex: 1,
      },
      {
        field: "item.productGroup.vatCode.code",
        valueGetter: (_, row) => row.item?.productGroup?.vatCode?.code || "-",
        headerName: t("invoiceJournalEntries:fieldVatCode"),
        flex: 1,
      },
      {
        field: "item.productGroup.account",
        valueGetter: (_, row) => row.item?.productGroup?.account || "-",
        headerName: t("invoiceJournalEntries:fieldAccount"),
        flex: 1,
      },
      {
        field: "customer.name",
        valueGetter: (_, row) => row.customer?.name || "-",
        headerName: t("invoiceJournalEntries:fieldCustomer"),
        flex: 1,
      },
      {
        field: "customer.customerNumber",
        valueGetter: (_, row) => row.customer?.customerNumber || "-",
        headerName: t("invoiceJournalEntries:fieldCustomerNumber"),
        flex: 1,
      },
      {
        field: "customer.organizationNumber",
        valueGetter: (_, row) => row.customer?.organizationNumber || "-",
        headerName: t("entity:contact:organizationNumber"),
        flex: 1,
      },
      {
        field: "orderDate",
        type: "date",
        valueGetter: (_, row) => new Date(row.orderDate),
        valueFormatter: (_, row) => moment(row.orderDate).format("YYYY.MM.DD"),
        headerName: t("invoiceJournalEntries:fieldOrderDate"),
        flex: 1,
      },
      {
        field: "customer.billing.address",
        valueGetter: (_, row) => row.customer?.billing?.address || "-",
        headerName: t("entity:location:address"),
        flex: 1,
      },
      {
        field: "customer.billing.area",
        valueGetter: (_, row) => row.customer?.billing?.area || "-",
        headerName: t("invoiceJournalEntries:fieldBillingCity"),
        flex: 1,
      },
      {
        field: "customer.billing.postNumber",
        valueGetter: (_, row) => row.customer?.billing?.postNumber || "-",
        headerName: t("entity:location:postCode"),
        flex: 1,
      },
    ]
  }, [t]);


  const ordersColumns = useMemo(() => {
    return [
      {
        field: "number",
        type: "number",
        headerName: t("entity:order:number"),
        flex: 0
      },
      {
        field: "missionNumber",
        type: "number",
        headerName: t("common:tables:mission"),
        flex: 0
      },
      {
        field: "customer.name",
        headerName: t("entity:order:customer"),
        valueGetter: (_, row) => row.customer?.name || "-",
        minWidth: 160,
        flex: 0.5,
      },
      {
        field: "payment.method",
        headerName: t("entity:order:payment:method"),
        valueGetter: (_, row) => t(`order:enums:paymentMethod:${OrderEnumsLists.PaymentMethod[row.payment.method]}`),
        minWidth: 160
      },
      {
        field: "payment.calculatedTotal.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:payment:calculatedTotal"),
        valueGetter: (_, row) => Number(row.payment.calculatedTotal?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => `${FixNumber(params.row.payment.calculatedTotal?.value)} ${getCurrencyShorthand(t, params.row.payment.calculatedTotal?.currency)}`,
        minWidth: 120
      },
      {
        field: "deductible.price.value",
        type: "number",
        entityType: "decimal",
        headerName: t("entity:order:deductible"),
        valueGetter: (_, row) => Number(row.deductible?.price?.value || "0"),
        valueFormatter: (value) => FixNumber(value),
        renderCell: (params) => params.row.deductible ? `${FixNumber(params.row.deductible?.price?.value)} ${getCurrencyShorthand(t, params.row.deductible?.price?.currency)}` : "-",
        minWidth: 120
      },
      {
        field: "payment.sender.name",
        headerName: t("entity:order:sender"),
        valueGetter: (_, row) => row.payment.sender?.name || "-",
        minWidth: 160,
        flex: 0.5
      },
    ]
  }, [t]);

  const onOrderRowClick = useCallback((params, ev) => {
    navigate(`./${params.row.missionId}/${params.id}`, { relative: "path" });
  }, [navigate]);

  const onJournalRowClick = useCallback((params, ev) => {
    navigate(`./${params.row.missionId}/${params.row.orderId}`, { relative: "path" });
  }, [navigate]);

  return (
    <ResponsivePage
      title={<>{t("invoiceExport:title")} <span className={classes.number}>#{exportEntity?.number || ""}</span></>}
      safeTitle={`${t("invoiceExport:title")} #${exportEntity?.number || ""}`}
      maxWidth={config.pageSize.large}
      backPath="../"
      backButton
    >

      <ToggleSurface
        title={t("invoiceExport:exportTitle")}
        xs={12}
        lg={6}
      >
        <Grid item xs={12} container spacing={2} alignItems="center">

          <Grid item xs={12}>
            <FormControl fullWidth size="small" variant="filled">
              <InputLabel>{t("invoiceExport:exportSelect")}</InputLabel>
              <Select onChange={(ev) => setFormat(ev.target.value)} value={format} disabled={working}>

                <ListSubheader>{t("invoiceExport:filesSubHeader")}</ListSubheader>
                {ExportEnumsLists.InvoiceExportFormats.map((type, value) => <MenuItem value={value} key={value}>{type}</MenuItem>)}

                {!!integrations.length ? <Divider /> : null}

                {!!integrations.length ? <ListSubheader>{t("invoiceExport:integrationsSubHeader")}</ListSubheader> : null}
                {integrations.map((ig, value) => <MenuItem value={value + FILE_FORMATS_LEN} key={value}>{ig.friendlyName}</MenuItem>)}

              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} lg={4}>
            <Button variant="contained" fullWidth size="large" onClick={triggerExport} disabled={journalLinesLoading || working}>
              {working ? t("invoiceExport:working") : format < FILE_FORMATS_LEN ? t("common:download") : t("invoiceExport:sendButton")}
            </Button>
          </Grid>

        </Grid>
      </ToggleSurface>

      <ConfirmDialog
        task={resendAction}
        title={t("integrations:resend:title")}
        description={t("integrations:resend:description")}
        confirmText={t("integrations:resend:confirmText")}
      />

      <ToggleSurface
        title={!!exportEntity?.history?.length ? t("invoiceExport:history") : null}
        xs={12}
        lg={6}
      >
        <ExportHistorySection
          loading={!exportEntity}
          history={exportEntity?.history || []}
        />
      </ToggleSurface>

      <ToggleSurface title={t("invoiceExport:orders")}>
        <SimpleTable
          tableId="invoiceexport_orders"
          loading={ordersLoading}
          onRowClick={onOrderRowClick}
          columns={ordersColumns}
          rows={orders}
          emptyStateTitle={t("invoiceExport:ordersEmptyStateTitle")}
          emptyStateDescription={t("invoiceExport:ordersEmptyStateDescription")}
          grow
          xs={12}
        />
      </ToggleSurface>

      <ToggleSurface title={t("invoiceExport:journalLines")}>
        <SimpleTable
          tableId="invoiceexport_journallines"
          loading={journalLinesLoading}
          onRowClick={onJournalRowClick}
          columns={journalLinesColumns}
          rows={journalLines}
          emptyStateTitle={t("cashJournal:emptyStateTitle")}
          emptyStateDescription={t("cashJournal:emptyStateDescription")}
          xs={12}
        />
      </ToggleSurface>

    </ResponsivePage>
  );
}

